import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { RedoSecondaryNavItem } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import PackageSearchIcon from "@redotech/redo-icons/package-search.svg";
import PackageSearchIconFilled from "@redotech/redo-icons/package-search_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoManagedReturnsRpcClientContext } from "@redotech/redo-merchant-managed-returns-app/rpc-client";
import { TaskDomain } from "@redotech/redo-task-common/task";
import { memo, useContext } from "react";
import { useLocation, useMatch } from "react-router-dom";

export const ManagedReturnsItem = memo(function ManagedReturnsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const location = useLocation();
  const managedReturnsRpc = useContext(RedoManagedReturnsRpcClientContext);

  const hasPermissionToViewItem = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
  );

  const anyChildSelected = useMatch(`${routes.managedReturnsHref}/*`);

  const showManagedReturns =
    !!team.settings.redoManagedReturns?.enabled && hasPermissionToViewItem;

  const unresolvedTasksLoad = useLoad(
    async (signal) => {
      if (!managedReturnsRpc || !showManagedReturns) {
        return undefined;
      }
      return managedReturnsRpc.getTasksMetrics(
        { domain: TaskDomain.RETURNS },
        { signal },
      );
    },
    [managedReturnsRpc, showManagedReturns, location.pathname],
  );

  const unresolvedTaskCount = unresolvedTasksLoad.value?.pendingTotal;
  const taskNotificationCount =
    unresolvedTaskCount !== undefined && unresolvedTaskCount > 0
      ? unresolvedTaskCount
      : undefined;

  if (!showManagedReturns) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getManagedReturnsSubRoute("inbound-shipments"),
      }}
      anyChildSelected={!!anyChildSelected}
      data-dd-action-name="returns_nav:managed_returns_link_click"
      FilledIcon={PackageSearchIconFilled}
      friendlyName={t("Managed returns", {
        context:
          "returns navigation label for warehouse-managed return processing",
      })}
      id="managed-returns"
      notificationCount={taskNotificationCount}
      UnfilledIcon={PackageSearchIcon}
    >
      <ManagedReturnsInboundShipmentsItem />
      <ManagedReturnsInventoryItem />
      <ManagedReturnsProductsItem />
      <ManagedReturnsServiceRequestsItem />
      <ManagedReturnsTasksItem notificationCount={taskNotificationCount} />
    </RedoSecondaryNavItem>
  );
});

const ManagedReturnsInboundShipmentsItem = memo(
  function ManagedReturnsInboundShipmentsItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getManagedReturnsSubRoute("inbound-shipments"),
        }}
        data-dd-action-name="returns_nav:managed_returns_inbound_shipments_link_click"
        friendlyName={t("Inbound shipments", {
          context: "returns management navigation label",
        })}
        id="managed-returns-inbound-shipments"
      />
    );
  },
);

const ManagedReturnsInventoryItem = memo(
  function ManagedReturnsInventoryItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getManagedReturnsSubRoute("inventory"),
        }}
        data-dd-action-name="returns_nav:managed_returns_inventory_link_click"
        friendlyName={t("Inventory", {
          context: "returns management navigation label",
        })}
        id="managed-returns-inventory"
      />
    );
  },
);

const ManagedReturnsProductsItem = memo(function ManagedReturnsProductsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getManagedReturnsSubRoute("products"),
      }}
      data-dd-action-name="returns_nav:managed_returns_products_link_click"
      friendlyName={t("Products", {
        context: "returns management navigation label",
      })}
      id="managed-returns-products"
    />
  );
});

const ManagedReturnsServiceRequestsItem = memo(
  function ManagedReturnsServiceRequestsItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getManagedReturnsSubRoute("service-requests"),
        }}
        data-dd-action-name="returns_nav:managed_returns_service_requests_link_click"
        friendlyName={t("Service requests", {
          context: "returns management navigation label",
        })}
        id="managed-returns-service-requests"
      />
    );
  },
);

const ManagedReturnsTasksItem = memo(function ManagedReturnsTasksItem({
  notificationCount,
}: {
  notificationCount?: number;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getManagedReturnsSubRoute("tasks") }}
      data-dd-action-name="returns_nav:managed_returns_tasks_link_click"
      friendlyName={t("Tasks", {
        context: "returns management navigation label",
      })}
      id="managed-returns-tasks"
      notificationCount={notificationCount}
    />
  );
});
